/**
 * Reine, DOM-freie Layout-Logik fürs anpassbare Panel-Grid (order + span +
 * visible auf einem 12-Spalten-Raster). Voll testbar; das UI (PanelGrid) und die
 * Persistenz (layouts/store) bauen darauf auf.
 */

export const SPANS = [3, 4, 6, 12] as const; // ¼ · ⅓ · ½ · voll
export type Span = (typeof SPANS)[number];

export interface PanelDef {
  id: string;
  defaultSpan: number;
  defaultVisible?: boolean;
}

export interface LayoutEntry {
  id: string;
  order: number;
  span: number;
  visible: boolean;
}

export function clampSpan(span: number, fallback = 4): number {
  if ((SPANS as readonly number[]).includes(span)) return span;
  return (SPANS as readonly number[]).includes(fallback) ? fallback : 4;
}

/**
 * Verschmilzt ein gespeichertes Layout mit den aktuellen Panel-Definitionen:
 * gespeicherte Werte (order/span/visible) gewinnen, NEUE Panels werden hinten
 * angehängt, im Speicher verbliebene aber entfernte Panels fallen weg. Die
 * `order` wird am Ende lückenlos 0..n neu vergeben.
 */
export function mergeLayout(saved: LayoutEntry[] | null, items: PanelDef[]): LayoutEntry[] {
  const savedMap = new Map((saved ?? []).map((e) => [e.id, e]));
  const merged: LayoutEntry[] = items.map((it, i) => {
    const s = savedMap.get(it.id);
    if (s) return { id: it.id, order: s.order, span: clampSpan(s.span, it.defaultSpan), visible: s.visible };
    return { id: it.id, order: 1e6 + i, span: clampSpan(it.defaultSpan, it.defaultSpan), visible: it.defaultVisible !== false };
  });
  merged.sort((a, b) => a.order - b.order);
  return merged.map((e, i) => ({ ...e, order: i }));
}

/** Verschiebt ein Panel an die Zielposition (über alle Einträge) + renummeriert. */
export function reorder(layout: LayoutEntry[], id: string, toIndex: number): LayoutEntry[] {
  const arr = [...layout].sort((a, b) => a.order - b.order);
  const from = arr.findIndex((e) => e.id === id);
  if (from < 0) return layout;
  const [item] = arr.splice(from, 1);
  const idx = Math.max(0, Math.min(toIndex, arr.length));
  arr.splice(idx, 0, item!);
  return arr.map((e, i) => ({ ...e, order: i }));
}

export function setSpan(layout: LayoutEntry[], id: string, span: number): LayoutEntry[] {
  return layout.map((e) => (e.id === id ? { ...e, span: clampSpan(span, e.span) } : e));
}

export function setVisible(layout: LayoutEntry[], id: string, visible: boolean): LayoutEntry[] {
  return layout.map((e) => (e.id === id ? { ...e, visible } : e));
}

export function resetToDefault(items: PanelDef[]): LayoutEntry[] {
  return mergeLayout(null, items);
}

/** Sichtbare Einträge in Order-Reihenfolge. */
export function visibleSorted(layout: LayoutEntry[]): LayoutEntry[] {
  return layout.filter((e) => e.visible).sort((a, b) => a.order - b.order);
}
